/* eslint-disable import/extensions */
import { Box } from '@mui/material';

import mergeRefs from 'merge-refs';
import {
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
    useCallback,
    useState,
    useLayoutEffect,
} from 'react';
import { APIManager, axios } from 'shared/manager';

import * as XLSX from 'xlsx';

import LinkService from 'shared/shim/react-pdf/LinkService';
import {
    NodeOrRenderer,
    ScrollPageIntoViewArgs,
} from 'shared/shim/react-pdf/shared/types';
import {
    useDebounce,
    useMeasure,
    useTimeoutFn,
    useUpdateEffect,
} from 'react-use';
import { Redacts } from 'shared/shim/react-pdf/PageCanvas';
import { Vector3 } from 'three';
import Message from 'shared/shim/react-pdf/Message';
import { DocumentPDF } from 'modules/home/manage-packets/solicitor-viewer/components/DocumentViewer';
import {
    csvArrayInfo,
    csvCell,
    csvCellInfo,
} from 'modules/home/manage-packets/solicitor-viewer/SolicitorDocumentPage';
import { AxiosError, CanceledError } from 'axios';
import { PageScrollTo } from 'shared/shim/react-pdf/Document';
import LuckyExcel from 'luckyexcel';
import FileSaver from 'file-saver';

export interface DocumentCSV extends DocumentPDF {
    resetSource: () => void;
    destroy?: () => void;
    setSheetZoom?: (number: any) => void;
    getExcelFile?: (filename: string) => void;
    pages: any;
}
interface PageViewportProps {
    height: number;
    scale: number;
    width: number;
}
export interface inferCSVRedact {
    getRedactData: () => Redacts[];
    setRedactData: (data: Redacts[] | null) => void;
    setRedactDataByIndex: (data: Redacts | null, index: number) => void;
    getRenderViewport: () => PageViewportProps;
    getTotalDrawnVertices: () => number;
    removeDrawnVertices: () => void;
    getViewport: () => PageViewportProps;
    scrollToSpecificRedact: (num: number) => void;
    scrollToSpecificVertices: (num: number) => void;
    scrollToSpecificCell: (row: number, column: number) => void;
    resetRenderer: () => void;
    reinitRenderer: () => void;
    disposeRenderer: () => void;
    getCellCSVData?: () => csvCellInfo[];
    getRangeCSVData?: () => csvArrayInfo[];
}
export interface loadSheet {
    numPages: number;
}
interface SheetViewer {
    onRefreshDocument: () => void;
    onScrolling: (value: boolean) => void;
    onLoadStart: () => void;
    onLoadCancelation: () => void;
    onLoadNetworkError: () => void;
    onSourceSuccess: () => void;
    onSourceError: () => void;
    onLoadSuccess: (sheet: loadSheet) => void;
    onErased?: (index: number) => void;
    onRemove?: (index: number) => void;
    onRenderSuccess: () => void;
    onChangeMeasure: () => void;
    onSheetChanged: (sheetForPage: number) => void;
    verticesData?: Redacts[];
    csvInfo: csvCellInfo[] | null;
    csvCellInfo?: csvCellInfo[];
    csvArrayInfo?: csvArrayInfo[];
    canvasRef?: React.Ref<HTMLCanvasElement>;
    canvasInnerRef?: React.Ref<HTMLCanvasElement>;
    redactRef: React.Ref<inferCSVRedact>;
    canDrawn: boolean;
    onDrawn?: (
        startpoint: THREE.Vector3,
        endpoint: THREE.Vector3,
        sheetNum: number
    ) => void;
    width: number;
    height: number;
    noData: NodeOrRenderer;
    loading: NodeOrRenderer;
    error: NodeOrRenderer;
    viewerId: any;
    file: string | null;
    className: string;
    inputRef: any;
    placeholder: string;
    ref: React.Ref<DocumentCSV>;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const LuckySheetViewer: React.FC<SheetViewer> = forwardRef(
    ({ ...props }, ref) => {
        const {
            viewerId,
            file,
            onScrolling,
            onLoadStart,
            onSourceError,
            onSourceSuccess,
            onLoadSuccess,
            onLoadCancelation,
            onLoadNetworkError,
            csvInfo,
            csvCellInfo: csvDataCellInfo,
            csvArrayInfo: csvDataArrayInfo,
            onRenderSuccess,
            onSheetChanged,
            onChangeMeasure,

            canvasInnerRef,
            width,
            height,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            verticesData,
            inputRef,
            className,
            redactRef,
            noData,
            loading,
            error,
            placeholder,
        } = props;

        const [enableViewer, setEnableViewer] = useState<boolean>(false);
        const blockRef = useRef<THREE.MeshStandardMaterial[]>([]);
        const shapeRef = useRef<THREE.MeshStandardMaterial[]>([]);
        const [data, setData] = useState<any>(null);
        const protocol = useRef<AbortController | null>(null);
        // const [colLength, setColumnLength] = useState<number>(0);
        const edgeShapeRef = useRef<Array<THREE.Mesh>>([]);
        const edgeBlockRef = useRef<Array<THREE.Mesh>>([]);
        const canvasElement = useRef<HTMLCanvasElement | null>(null);
        const canvasWebGLElement = useRef<HTMLCanvasElement>(null);
        const [arrayRedact, setRedacts] = useState<Redacts[]>([]);
        const [enableCanvas, setEnableCanvas] = useState<boolean>(false);
        const [currentSheetNum, setSheetNum] = useState<number>(0);
        const [isSheetLoaded, setSheetLoaded] = useState<boolean>(false);
        const [renderViewport, setRenderViewport] = useState<PageViewportProps>(
            { height: 0, scale: 0, width: 0 }
        );

        const [viewport, setViewport] = useState<PageViewportProps>({
            height,
            scale: 1,
            width,
        });
        const [selectedCellHighlight, setSelectedCellHighlight] = useState<
            csvCellInfo[]
        >([]);
        const [selectedArrayHighlight, setSelectedArrayHighlight] = useState<
            csvArrayInfo[]
        >([]);

        const [isREtexture, setReDrawnTexture] = useState<boolean>(false);
        const [arraySheet, setSheets] = useState<any[]>([]);
        const [isLoadedTexture, setLoadedTexture] = useState<boolean>(false);
        const viewerRef = useRef<HTMLDivElement | null>(null);
        const [isNoData, setNoData] = useState<boolean>(true);
        const [isLoading, setLoading] = useState<boolean>(false);
        const [isError, setError] = useState<boolean>(false);
        const [sheetData, setSheetData] = useState<Array<any>>([]);
        const dummyLinkService = useRef<LinkService | null>(null);
        const [updateRenderSelector, setUpdateRenderSelector] =
            useState<boolean>(false);
        const dummyScrollPage = useRef<{
            scrollPageIntoView: (args: ScrollPageIntoViewArgs) => void;
            scrollTo: (args: PageScrollTo) => void;
        } | null>(null);

        const handleRangeSelectionToCell = useCallback(
            (array: csvArrayInfo[]): csvCell[] => {
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                const RANGE = (x: number, y: number) =>
                    Array.from(
                        (function* () {
                            // eslint-disable-next-line no-plusplus, no-param-reassign
                            while (x <= y) yield x++;
                        })()
                    );
                const csvCell: csvCell[] = [];
                // eslint-disable-next-line no-plusplus
                for (let index = 0; index < array.length; index++) {
                    const e = array[index];
                    const column_range = RANGE(
                        e.column.at(0) ?? 0,
                        e.column.at(1) ?? 0
                    );
                    const row_range = RANGE(e.row.at(0) ?? 0, e.row.at(1) ?? 0);

                    // eslint-disable-next-line no-plusplus
                    for (let r = 0; r < column_range.length; r++) {
                        const c = column_range[r];
                        // eslint-disable-next-line no-plusplus
                        for (let d = 0; d < row_range.length; d++) {
                            const r = row_range[d];
                            csvCell.push({
                                row: r,
                                column: c,
                                Term: '',
                            });
                        }
                    }
                }

                return csvCell;
            },
            []
        );
        const [filteredCSVInfo, setFilteredCSVInfo] = useState<csvCellInfo[]>(
            []
        );

        const luckyCss: any = {
            margin: '0px',
            padding: '0px',
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: '0px',
            top: '0px',
        };
        const [
            measureRef,
            {
                x: measureX,
                y: measureY,
                width: measureWidth,
                height: measureHeight,
                top: measureTop,
                right: measureRight,
                bottom: measureBottom,
                left: measureLeft,
            },
        ] = useMeasure<HTMLDivElement>();
        useEffect(() => {
            if (typeof onChangeMeasure === 'function') {
                onChangeMeasure();
            }
        }, [
            measureX,
            measureY,
            measureWidth,
            measureHeight,
            measureTop,
            measureRight,
            measureBottom,
            measureLeft,
            onChangeMeasure,
        ]);

        const handleResizeComponent = useCallback(
            (size: any) => {
                setRenderViewport(size);
                const { current: canvasOverlay } = canvasWebGLElement;

                if (!canvasOverlay || !isLoadedTexture) {
                    return;
                }

                canvasOverlay.width = size.width;
                canvasOverlay.height = size.height;
                canvasOverlay.style.width = `${Math.floor(size.width)}px`;
                canvasOverlay.style.height = `${Math.floor(size.height)}px`;
            },
            [isLoadedTexture]
        );
        const [isScrolling, setScrolling] = useState<boolean>(false);
        const [,] = useDebounce(
            () => {
                setScrolling(false);
            },
            2000,
            [isScrolling]
        );
        const handleScrolling = useCallback(() => {
            setScrolling(true);
        }, []);
        useLayoutEffect(() => {
            if (typeof onScrolling === 'function') {
                onScrolling(isScrolling);
            }
        }, [isScrolling]);
        useLayoutEffect(() => {
            if (
                isLoading === false &&
                isError === false &&
                isNoData === false
            ) {
                setViewport({ width, height, scale: 1 });
                if (typeof onRenderSuccess === 'function') {
                    onRenderSuccess();
                }
                setUpdateRenderSelector(true);
                const rootCanvas: HTMLCanvasElement | null =
                    document.querySelector(`#${viewerId} canvas`);
                if (rootCanvas) {
                    canvasElement.current = rootCanvas;
                    mergeRefs(canvasElement, canvasInnerRef);
                }
            }
        }, [enableCanvas]);
        const handleUpdatedCanvasExcel = useCallback(() => {
            // setEnableCanvas(true);
            setReDrawnTexture(true);
            setLoadedTexture(true);
        }, []);
        const handleSheetBefore = useCallback(() => {
            setLoadedTexture(false);
            setSheetLoaded(false);
            setRedacts([]);
        }, []);
        const handleErrorDetected = useCallback(() => {
            if (enableCanvas === false || isSheetLoaded === false) {
                setError(true);
            } else {
                setError(false);
            }
        }, [enableCanvas, isSheetLoaded]);
        const [, cancel, reset] = useTimeoutFn(handleErrorDetected, 5000);
        useEffect(() => {
            cancel();
        }, []);
        useLayoutEffect(() => {
            if (enableCanvas) reset();
        }, [enableCanvas]);
        const [isLoadError, setLoadError] = useState<boolean>(false);
        useLayoutEffect(() => {
            if (isLoadError) {
                if (enableCanvas === false || isSheetLoaded === false) {
                    setError(true);
                }
                setLoadError(false);
            }
        }, [isLoadError]);

        const handleSheetChange = useCallback(
            (sheet: any) => {
                const sheetIndex = arraySheet.findIndex(
                    (e) => e.name === sheet.name
                );
                setSheetNum(sheetIndex);
                setSheetLoaded(true);
                if (typeof onSheetChanged === 'function') {
                    onSheetChanged(sheetIndex);
                }
                setLoadedTexture(true);
            },
            [arraySheet, onSheetChanged]
        );

        const handleConvertToBorderInfo = useCallback((e: csvCellInfo) => {
            if (typeof e.row === 'number' && typeof e.column === 'number') {
                return {
                    rangeType: 'cell',
                    value: {
                        row_index: e.row,
                        col_index: e.column - 1,
                        l: {
                            style: 10,
                            color: 'rgb(25, 30, 68)',
                        },
                        r: {
                            style: 10,
                            color: 'rgb(25, 30, 68)',
                        },
                        t: {
                            style: 10,
                            color: 'rgb(25, 30, 68)',
                        },
                        b: {
                            style: 10,
                            color: 'rgb(25, 30, 68)',
                        },
                    },
                };
            }
            return {
                rangeType: 'range',
                borderType: 'border-all',
                style: '3',
                color: 'rgb(25, 30, 68)',
                range: [
                    {
                        row: e.row,
                        column: Array.from(
                            typeof e.column !== 'number' ? e.column : []
                        ).map((x) => x - 1),
                    },
                ],
            };
        }, []);

        const isWhitespaceString = (str: string): boolean =>
            !str.replace(/\s/g, '').length;
        const handleConvertToConditionFormat = useCallback((e: csvCell) => {
            const { luckysheet } = window;
            const value = luckysheet.getCellValue(e.row, e.column - 1);

            if (
                typeof value === 'string' &&
                (isWhitespaceString(value) || value === '')
            ) {
                return {
                    type: 'default',
                    cellrange: [
                        {
                            row: [e.row, e.row],
                            column: [e.column - 1, e.column - 1],
                        },
                    ],
                    format: {
                        textColor: '#000000',
                        cellColor: '#FFC808',
                    },
                    conditionName: 'duplicateValue',
                    conditionRange: [
                        {
                            row: [e.row, e.row],
                            column: [e.column - 1, e.column - 1],
                        },
                    ],
                    conditionValue: '1',
                };
            }
            if (typeof value === 'object' && value === null) {
                return {
                    type: 'default',
                    cellrange: [
                        {
                            row: [e.row, e.row],
                            column: [e.column - 1, e.column - 1],
                        },
                    ],
                    format: {
                        textColor: '#000000',
                        cellColor: '#FFC808',
                    },
                    conditionName: 'duplicateValue',
                    conditionRange: [
                        {
                            row: [e.row, e.row],
                            column: [e.column - 1, e.column - 1],
                        },
                    ],
                    conditionValue: '2',
                };
            }
            return {
                type: 'default',
                cellrange: [
                    {
                        row: [e.row, e.row],
                        column: [e.column - 1, e.column - 1],
                    },
                ],
                format: {
                    textColor: '#000000',
                    cellColor: '#FFC808',
                },
                conditionName: 'textContains',
                conditionRange: [
                    {
                        row: [e.row, e.row],
                        column: [e.column - 1, e.column - 1],
                    },
                ],
                conditionValue: [value],
            };
        }, []);

        useUpdateEffect(() => {
            const { luckysheet } = window;
            if (updateRenderSelector) {
                const generatesheetData = Array.from(sheetData).map((e) => ({
                    ...e,
                    config: {
                        borderInfo: Array.from(csvInfo || [])
                            .map(handleConvertToBorderInfo)
                            // .concat(
                            //     Array.from(csvDataArrayInfo || []).map(
                            //         handleConvertToBorderInfo
                            //     )
                            // )
                            // .concat(
                            //     Array.from(csvDataCellInfo || []).map(
                            //         handleConvertToBorderInfo
                            //     )
                            // )
                            .concat(
                                Array.from(selectedCellHighlight).map(
                                    handleConvertToBorderInfo
                                )
                            )
                            .concat(
                                Array.from(selectedArrayHighlight).map(
                                    handleConvertToBorderInfo
                                )
                            ),
                    },
                }));

                const luckysheetfile = luckysheet.getluckysheetfile();
                const currentsheetData = generatesheetData[currentSheetNum];
                luckysheetfile[0].config.borderInfo =
                    currentsheetData.config.borderInfo;
                luckysheetfile[0].luckysheet_conditionformat_save = Array.from(
                    (filteredCSVInfo as csvCell[]) || []
                )
                    .map(handleConvertToConditionFormat)
                    // .concat(
                    //     Array.from(
                    //         handleRangeSelectionToCell(csvDataArrayInfo || [])
                    //     ).map(handleConvertToConditionFormat)
                    // )
                    // .concat(
                    //     Array.from((csvDataCellInfo as csvCell[]) || []).map(
                    //         handleConvertToConditionFormat
                    //     )
                    // )
                    .concat(
                        Array.from(selectedCellHighlight as csvCell[]).map(
                            handleConvertToConditionFormat
                        )
                    )
                    .concat(
                        Array.from(
                            handleRangeSelectionToCell(selectedArrayHighlight)
                        ).map(handleConvertToConditionFormat)
                    );
                // luckysheet.buildGridData(currentsheetData);
                // luckysheet.sheetmanage.mergeCalculation(currentsheetData.index);
                // luckysheet.sheetmanage.setSheetParam();
                // luckysheet.sheetmanage.showSheet();
                // luckysheet.sheetmanage.restoreCache();
                // formula.execFunctionGroupForce(
                //     luckysheetConfigsetting.forceCalculation
                // );

                luckysheet.refresh();
                // luckysheet.refreshFormula();
                // luckysheet.jfrefreshgrid();
                // luckysheet.luckysheetrefreshgrid(width, height);
                setUpdateRenderSelector(false);
            }
        }, [updateRenderSelector]);
        const [updateRangeSelect, setUpdateRangeSelect] =
            useState<boolean>(false);
        const [updateSelector, setSelector] = useState<any[] | null>(null);

        useEffect(() => {
            if (
                updateRangeSelect &&
                updateSelector &&
                Array.isArray(updateSelector) &&
                updateSelector.length > 0
            ) {
                const selector = updateSelector;

                if (enableCanvas) {
                    if (
                        selector.some(
                            (x) =>
                                x.column.join(',') !==
                                    `${x.column[0]},${x.column[0]}` ||
                                x.row.join(',') !== `${x.row[0]},${x.row[0]}`
                        )
                    ) {
                        const arrColumn = Array.from(selector.at(0).column).map(
                            (x: any) => x + 1
                        );
                        const arrRow = selector.at(0).row;
                        const column = arrColumn.join(',');
                        const row = arrRow.join(',');

                        if (
                            selectedArrayHighlight.some(
                                (e) =>
                                    e.column.join(',') === column &&
                                    e.row.join(',') === row
                            )
                        ) {
                            setSelectedArrayHighlight((prev) => {
                                const updatedHiglight = prev;

                                return updatedHiglight.filter(
                                    (e) =>
                                        e.column.join(',') !== column &&
                                        e.row.join(',') !== row
                                );
                            });
                        } else {
                            setSelectedArrayHighlight((prev) => {
                                const updatedHiglight = prev;
                                updatedHiglight.push({
                                    row: arrRow,
                                    column: arrColumn,
                                    Term: '',
                                });
                                return updatedHiglight;
                            });
                        }
                        // range
                    } else {
                        // single cell
                        const column = selector.at(0).column[0];
                        const row = selector.at(0).row[0];

                        if (
                            selectedCellHighlight.some(
                                (e) =>
                                    typeof e.column === 'number' &&
                                    e.column - 1 === column &&
                                    e.row === row
                            )
                        ) {
                            setSelectedCellHighlight((prev) => {
                                const updatedHiglight = prev;

                                const highlightIndex =
                                    updatedHiglight.findIndex(
                                        (e) =>
                                            typeof e.column === 'number' &&
                                            e.column - 1 === column &&
                                            e.row === row
                                    );
                                return updatedHiglight.filter(
                                    (x, i) => i !== highlightIndex
                                );
                            });
                            // have selected highlight;
                        } else {
                            setSelectedCellHighlight((prev) => {
                                const updatedHiglight = prev;
                                updatedHiglight.push({
                                    row,
                                    column: column + 1,
                                    Term: '',
                                });
                                return updatedHiglight;
                            });
                            // not having selected highlight
                        }
                    }
                }
                setSelector(null);
                setUpdateRenderSelector(true);
                setUpdateRangeSelect(false);
            }
        }, [updateRangeSelect]);
        const handleRangeSelected = useCallback(() => {
            setUpdateRangeSelect(true);
        }, []);
        const handleRangeSelect = useCallback((sheet: any, selector: any) => {
            setSelector(selector);
            setUpdateRangeSelect(false);
        }, []);
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const loadExcel = useCallback(
            (
                sheets: any[],
                cellInfo?: csvCellInfo[],
                arrayInfo?: csvArrayInfo[]
            ) => {
                const { luckysheet } = window;
                const updatedArrayhighlight = selectedArrayHighlight.concat(
                    arrayInfo || []
                );
                const updatedCellhighlight = selectedCellHighlight.concat(
                    cellInfo || []
                );
                const SheetBordered = filteredCSVInfo
                    ? Array.from(sheets).map((e) => ({
                          ...e,
                          zoomRatio: 0.9, // 缩放比例
                          config: {
                              borderInfo: Array.from(filteredCSVInfo)
                                  .map(handleConvertToBorderInfo)
                                  .concat(
                                      Array.from(
                                          updatedArrayhighlight || []
                                      ).map(handleConvertToBorderInfo)
                                  )
                                  .concat(
                                      Array.from(
                                          updatedCellhighlight || []
                                      ).map(handleConvertToBorderInfo)
                                  ),
                              authority: {
                                  selectLockedCells: 1,
                                  selectunLockedCells: 1,
                                  formatCells: 0,
                                  formatColumns: 0,
                                  formatRows: 0,
                                  insertColumns: 0,
                                  insertRows: 0,
                                  insertHyperlinks: 0,
                                  deleteColumns: 0,
                                  deleteRows: 0,
                                  sort: 1,
                                  filter: 1,
                                  usePivotTablereports: 0,
                                  editObjects: 0,
                                  editScenarios: 0,
                                  sheet: 1,
                              },
                          },
                      }))
                    : Array.from(sheets).map((e) => ({
                          ...e,
                          zoomRatio: 0.9, // 缩放比例
                          config: {
                              borderInfo: Array.from(
                                  updatedArrayhighlight || []
                              )
                                  .map(handleConvertToBorderInfo)
                                  .concat(
                                      Array.from(
                                          updatedCellhighlight || []
                                      ).map(handleConvertToBorderInfo)
                                  ),
                              authority: {
                                  selectLockedCells: 1,
                                  selectunLockedCells: 1,
                                  formatCells: 0,
                                  formatColumns: 0,
                                  formatRows: 0,
                                  insertColumns: 0,
                                  insertRows: 0,
                                  insertHyperlinks: 0,
                                  deleteColumns: 0,
                                  deleteRows: 0,
                                  sort: 1,
                                  filter: 1,
                                  usePivotTablereports: 0,
                                  editObjects: 0,
                                  editScenarios: 0,
                                  sheet: 1,
                              },
                          },
                      }));
                setSheetData(SheetBordered);
                setSelectedCellHighlight(updatedCellhighlight);
                setSelectedArrayHighlight(updatedArrayhighlight);

                luckysheet.create({
                    title: placeholder,
                    lang: 'en',
                    container: viewerId,
                    //   plugins: ["chart"],
                    allowUpdate: true,
                    updateUrl: true,
                    options: {
                        userInfo: false,
                    },
                    data: SheetBordered,
                    myFolderUrl: '/',
                    userMenuItem: [],
                    showtoolbarConfig: {
                        exportXlsx: false,
                        undoRedo: false,
                        paintFormat: false,
                        currencyFormat: false,
                        percentageFormat: false,
                        numberDecrease: false,
                        numberIncrease: false,
                        moreFormats: false,
                        font: false,
                        fontSize: false,
                        bold: false,
                        italic: false,
                        strikethrough: false,
                        underline: false,
                        textColor: false,
                        fillColor: false,
                        border: false,
                        mergeCell: false,
                        horizontalAlignMode: false,
                        verticalAlignMode: false,
                        textWrapMode: false,
                        textRotateMode: false,
                        image: false,
                        link: false,
                        chart: false,
                        postil: false,
                        pivotTable: false,
                        function: false,
                        frozenMode: false,
                        sortAndFilter: false,
                        conditionalFormat: false,
                        dataVerification: false,
                        splitColumn: false,
                        screenshot: false,
                        findAndReplace: true,
                        protection: false,
                        print: false,
                    },
                    showinfobar: false,
                    showsheetbar: true,
                    showsheetbarConfig: {
                        add: false,
                        menu: true,
                        sheet: true,
                    },
                    allowCopy: false,
                    showstatisticBar: false,
                    showstatisticBarConfig: {
                        count: false, // Count bar
                        view: false, // Print view
                        zoom: false, // Zoom
                    },
                    enableAddRow: false,
                    enableAddBackTop: true,
                    showConfigWindowResize: false,
                    cellRightClickConfig: {
                        copy: false,
                        copyAs: false,
                        paste: false,
                        insertRow: false,
                        insertColumn: false,
                        deleteRow: false,
                        deleteColumn: false,
                        deleteCell: false,
                        hideRow: false,
                        hideColumn: false,
                        rowHeight: false,
                        columnWidth: false,
                        clear: false,
                        matrix: false,
                        sort: false,
                        filter: false,
                        chart: false,
                        image: false,
                        link: false,
                        data: false,
                        cellFormat: false,
                        selectHighlight: true,
                    },
                    sheetRightClickConfig: {
                        delete: false,
                        copy: false,
                        rename: false,
                        color: false,
                        hide: false,
                        move: false,
                    },
                    sheetFormulaBar: false,
                    hook: {
                        scroll: handleScrolling,
                        updated: handleUpdatedCanvasExcel,
                        resized: handleResizeComponent,
                        workbookCreateAfter: () => setEnableCanvas(true),
                        // cellMousedownBefore: () => console.log('mouse down'),
                        // cellMousedown: () => console.log('mouse down'),
                        // sheetMousemove: () => console.log('sheet move'),
                        // sheetMouseup: () => console.log('sheet up'),
                        selectorHighlightClicked: handleRangeSelected,
                        rangeSelect: handleRangeSelect,
                        // sheetActivateAfter: () => console.log('sheet active'),
                        // sheetCreateBefore: () => console.log('sheet create'),
                        // sheetCreateAfter: () => console.log('sheet after'),
                        sheetShowBefore: handleSheetBefore,
                        sheetShowAfter: handleSheetChange,
                        // sheetDeleteBefore: () =>
                        //     console.log('sheet delete before'),
                        // sheetDeleteAfter: () =>
                        //     console.log('sheet delete after'),
                        // sheetZoomBefore: () => console.log('zoom before'),
                        // sheetZoomAfter: () => console.log('zoom after'),
                    },
                });
                if (typeof onLoadSuccess === 'function') {
                    onLoadSuccess({ numPages: sheets.length });
                }

                setLoading(false);
            },
            [
                filteredCSVInfo,
                handleConvertToBorderInfo,
                handleRangeSelect,
                handleRangeSelected,
                handleResizeComponent,
                handleScrolling,
                handleSheetBefore,
                handleSheetChange,
                handleUpdatedCanvasExcel,
                onLoadSuccess,
                placeholder,
                selectedArrayHighlight,
                selectedCellHighlight,
                viewerId,
            ]
        );
        useImperativeHandle(
            ref,
            () => ({
                linkService: dummyLinkService,
                viewer: dummyScrollPage,
                resetSource: () => {},
                pages: undefined,
                destroy: () => {
                    setEnableViewer(false);
                    const { luckysheet } = window;
                    // luckysheet.setConfig({
                    //     allowUpdate: false,
                    //     updateUrl: false,
                    //     data: [],
                    //     hook: null,
                    // });
                    luckysheet.closeWebsocket();

                    luckysheet.destroy();
                },
                setSheetZoom: (num) => {
                    const { luckysheet } = window;
                    luckysheet.setSheetZoom(num + 0.8);
                },
                getExcelFile: (fileName) => {
                    FileSaver.saveAs(data, fileName);
                },
            }),
            [data]
        );
        useImperativeHandle(
            redactRef,
            () => ({
                getCellCSVData: () => selectedCellHighlight,
                getRangeCSVData: () => selectedArrayHighlight,
                disposeRenderer: () => {},
                resetRenderer: () => {
                    setEnableCanvas(false);
                },
                getSheetData: () => {},
                reinitRenderer: () => {},
                /**
                 * Get current active drawn vertices data
                 * @return {Redacts[]} array of redact object
                 */
                getRedactData: () => arrayRedact,
                /**
                 * Set current active drawn vertices data
                 */
                setRedactData: (data: Redacts[] | null) => {
                    if (data) {
                        setRedacts(arrayRedact.concat(data));
                    } else {
                        setRedacts([]);
                    }
                    setReDrawnTexture(true);
                },
                /**
                 * Set current active drawn vertices data by index
                 */
                setRedactDataByIndex: (data: Redacts | null, index: number) => {
                    if (data) {
                        setRedacts(
                            Array.from(arrayRedact).map((o, i) => {
                                if (i === index) {
                                    return data;
                                }
                                return o;
                            })
                        );
                    } else {
                        setRedacts(arrayRedact.filter((o, i) => i !== index));
                    }
                    setReDrawnTexture(true);
                },
                removeDrawnVertices: () => {
                    setRedacts([]);
                },
                /**
                 * Get total drawn count
                 * @return {number} length of the stored vertices in array
                 */
                getTotalDrawnVertices: () => arrayRedact.length,
                /**
                 * Get renderer viewport
                 * @return {PageViewport} page viewport with association to display pixel density
                 */
                getRenderViewport: () => renderViewport,
                /**
                 * Get renderer viewport
                 * @return {PageViewport} page viewport with association to window size
                 */
                getViewport: () => viewport,
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                scrollToSpecificRedact: (num: number) => {},
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                scrollToSpecificVertices: (num: number) => {},
                scrollToSpecificCell: (row: number, column: number) => {
                    const { luckysheet } = window;
                    if (luckysheet) {
                        luckysheet.scroll({
                            targetRow: row,
                            targetColumn: column,
                        });
                    }
                },
            }),
            [
                arrayRedact,
                renderViewport,
                selectedArrayHighlight,
                selectedCellHighlight,
                viewport,
            ]
        );

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const readExcelContent = useCallback(
            (file: Blob) => {
                const reader = new FileReader();

                reader.onload = (e) => {
                    if (typeof onLoadStart === 'function') {
                        onLoadStart();
                    }
                    if (e.target) {
                        const workbook = XLSX.read(e.target.result, {
                            type: 'binary',
                            cellFormula: true,
                            cellNF: true,
                            cellText: true,
                            dateNF: 'dd-MMM-yyy', // Specif
                        });

                        const sheets: any[] = [];
                        workbook.SheetNames.forEach((sheetName, i) => {
                            const sheetData: any[] = XLSX.utils.sheet_to_json(
                                workbook.Sheets[sheetName],
                                {
                                    raw: false,
                                    rawNumbers: false,
                                    header: 1,
                                    dateNF: 'dd-MMM-yyy', // Specify the date format string here
                                }
                            );

                            const colLength = sheetData[0].length;

                            const filteredCSVINFO = handleRangeSelectionToCell(
                                // eslint-disable-next-line @typescript-eslint/no-shadow
                                Array.from(csvInfo ?? []).map((e) => ({
                                    ...e,
                                    row:
                                        typeof e.row === 'number'
                                            ? [e.row, e.row]
                                            : e.row,
                                    column: [0, colLength],
                                }))
                            );

                            setFilteredCSVInfo(filteredCSVINFO);
                            sheets.push({
                                name: sheetName,
                                color: '',
                                status: '1',
                                order: i,
                                data: sheetData,
                                config: {},
                                index: i,
                            });
                        });
                        setSheets(sheets);
                        loadExcel(sheets, csvDataCellInfo, csvDataArrayInfo);
                    }
                };
                reader.readAsBinaryString(file);
            },
            [
                csvDataArrayInfo,
                csvDataCellInfo,
                csvInfo,
                handleRangeSelectionToCell,
                loadExcel,
                onLoadStart,
            ]
        );
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const loadExcelContent = useCallback(
            (_sheet: any) => {
                const arrayInfo = Array.from(csvInfo ?? []).map((e) => ({
                    ...e,
                    row: typeof e.row === 'number' ? [e.row, e.row] : e.row,
                    column:
                        typeof e.column === 'number'
                            ? [e.column, e.column]
                            : e.column,
                }));
                const filteredCSVINFO = handleRangeSelectionToCell(arrayInfo);

                setFilteredCSVInfo(filteredCSVINFO);
                setSheets(_sheet);
                loadExcel(_sheet, csvDataCellInfo, csvDataArrayInfo);
            },
            [csvDataArrayInfo, csvDataCellInfo, loadExcel]
        );

        const handleAbortProtocol = useCallback(() => {
            if (protocol.current) {
                protocol.current.abort();
            }
        }, [protocol]);
        useEffect(
            () => () => {
                handleAbortProtocol();
            },
            []
        );
        useEffect(() => {
            if (file) {
                setEnableCanvas(false);
                setEnableViewer(true);
                setLoading(true);
                handleAbortProtocol();
                const controller = new AbortController();
                protocol.current = controller;
                APIManager.sendGet(
                    file,
                    false,
                    {},
                    { responseType: 'blob', signal: controller.signal },
                    axios
                )
                    .then((response) => {
                        if (file.includes('.csv')) {
                            readExcelContent(response.data);
                        } else if (file.includes('.xlsx')) {
                            LuckyExcel.transformExcelToLucky(
                                response.data,
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                (exportJson: any, luckysheetfile) => {
                                    loadExcelContent(exportJson.sheets);

                                    // Get the worksheet data after conversion
                                },
                                // eslint-disable-next-line @typescript-eslint/no-shadow, @typescript-eslint/no-unused-vars
                                (error) => {
                                    // handle error if any thrown
                                }
                            );
                        }
                        setData(response.data);

                        if (typeof onSourceSuccess === 'function') {
                            onSourceSuccess();
                        }
                        setNoData(false);
                    })
                    .catch((error) => {
                        if (error instanceof CanceledError === false) {
                            if (
                                error !== null &&
                                error instanceof AxiosError &&
                                error.code === 'ERR_NETWORK'
                            ) {
                                if (typeof onLoadNetworkError === 'function') {
                                    onLoadNetworkError();
                                }
                                console.log('load error');
                                setLoadError(true);
                            }

                            if (typeof onSourceError === 'function') {
                                onSourceError();
                            }
                            setLoading(false);
                            setNoData(true);
                        } else if (
                            error instanceof CanceledError === true &&
                            typeof onLoadCancelation === 'function'
                        ) {
                            console.log('not canceled');
                            onLoadCancelation();
                        }
                    });
            }
        }, [file]);

        useLayoutEffect(() => {
            const { current: arrayShapes } = shapeRef;
            const { current: arrayBlock } = blockRef;
            const { current: arrayEdgeShapes } = edgeShapeRef;
            const { current: arrayEdgeBlock } = edgeBlockRef;

            arrayShapes.forEach((el) => {
                const texture = el;
                texture.needsUpdate = true;
            });
            arrayBlock.forEach((el) => {
                const texture = el;
                texture.needsUpdate = true;
            });
            arrayEdgeShapes.forEach((el) => {
                const texture = el;
                texture.updateMatrix();
                texture.updateMatrixWorld();
                texture.matrixWorldNeedsUpdate = true;
                texture.updateMorphTargets();
                texture.updateWorldMatrix(true, true);
                // texture.updateMatrix()
            });
            arrayEdgeBlock.forEach((el) => {
                const texture = el;
                texture.updateMatrix();
                texture.updateMatrixWorld();
                texture.matrixWorldNeedsUpdate = true;
                texture.updateMorphTargets();
                texture.updateWorldMatrix(true, true);
                // texture.updateMatrix()
            });
            if (isREtexture) {
                setReDrawnTexture(false);
            }
        }, [isREtexture, width, height]);
        return (
            <Box
                sx={{ width: '100%', height: '100%' }}
                className={`sheetViewer ${className}`}
                ref={measureRef}
            >
                {!file && isNoData && (
                    <Message type="no-data">
                        {typeof noData === 'function' ? noData() : noData}
                    </Message>
                )}
                {isLoading && (
                    <Message type="loading">
                        {typeof loading === 'function' ? loading() : loading}
                    </Message>
                )}

                {isError && (
                    <Message type="error">
                        {typeof error === 'function' ? error() : error}
                    </Message>
                )}

                {enableViewer && (
                    <Box>
                        <div
                            ref={mergeRefs(viewerRef, inputRef)}
                            id={viewerId}
                            style={luckyCss}
                        />
                    </Box>
                )}
            </Box>
        );
    }
);

export default LuckySheetViewer;
